import { DEFAULT_RULES_COURSE_TYPES } from "@services/constants/settings";
import {
  AdaptedCourseTypeAssignment,
  InstructorRemovalValidationSetting,
  InstructorLinkTagsSetting,
  RuleCourseTypeSetting,
} from "@services/types/settings/settingsTypes";
import { create } from "zustand";

const DEFAULT_INSTRUCTOR_LINK_TAGS: InstructorLinkTagsSetting = {
  enabled: false,
  tags: [],
};

export const DEFAULT_INSTRUCTOR_REMOVAL_VALIDATION: InstructorRemovalValidationSetting = {
  enabled: false,
};

type State = {
  ruleCourseTypeSetting: RuleCourseTypeSetting;
  adaptedAssignmentsByCourseType?: {
    [courseType: string]: { [key: string]: AdaptedCourseTypeAssignment };
  };
  showRetakersColumns: boolean;
  showContractedBlocksInSchedule: boolean;
  showKeepSessionDurationCheckbox: boolean;
  isAgentsChatEnabled: boolean;
  isProgrammingStructuresEnabled?: boolean;
  instructorLinkTags: InstructorLinkTagsSetting;
  validateInstructorsAfterRemoval: InstructorRemovalValidationSetting;
  canPublishPackages?: boolean;
};

export const useSettingsStore = create<State>(() => ({
  ruleCourseTypeSetting: DEFAULT_RULES_COURSE_TYPES,
  adaptedAssignmentsByCourseType: {},
  showRetakersColumns: false,
  showContractedBlocksInSchedule: false,
  showKeepSessionDurationCheckbox: true,
  isAgentsChatEnabled: false,
  isProgrammingStructuresEnabled: false,
  instructorLinkTags: DEFAULT_INSTRUCTOR_LINK_TAGS,
  validateInstructorsAfterRemoval: DEFAULT_INSTRUCTOR_REMOVAL_VALIDATION,
  canPublishPackages: false,
}));

export const setShowRetakersColumns = (value: boolean) => {
  useSettingsStore.setState({ showRetakersColumns: value });
};

export const setShowContractedBlocksInSchedule = (value: boolean) => {
  useSettingsStore.setState({ showContractedBlocksInSchedule: value });
};

export const setShowKeepSessionDurationCheckbox = (value: boolean) => {
  useSettingsStore.setState({ showKeepSessionDurationCheckbox: value });
};

export const setInstructorLinkTags = (value: InstructorLinkTagsSetting) => {
  useSettingsStore.setState({ instructorLinkTags: value ?? DEFAULT_INSTRUCTOR_LINK_TAGS });
};

export const setValidateInstructorsAfterRemoval = (value: InstructorRemovalValidationSetting) => {
  useSettingsStore.setState({
    validateInstructorsAfterRemoval: value ?? DEFAULT_INSTRUCTOR_REMOVAL_VALIDATION,
  });
};

export const setIsAgentsChatEnabled = (value: boolean) => {
  useSettingsStore.setState({ isAgentsChatEnabled: value });
};

/**
 * @param setting - The RuleCourseTypeSetting to be adapted and stored
 */
export const setRuleCourseTypeSetting = (setting: RuleCourseTypeSetting) => {
  const adaptedAssignmentsByCourseType: {
    [courseType: string]: { [key: string]: AdaptedCourseTypeAssignment };
  } = {};
  const configInstructorsLinks = setting?.enabled
    ? setting?.rules_course_types?.config_instructors_links
    : [];

  /**
   * Adapt assignments from nested structure to flat structure
   * to avoid duplicates and ease the access
   */
  configInstructorsLinks.forEach(link => {
    /**
     * Important: Course type link should always be in lowercase
     */
    const courseTypeLink = link?.course_type_link?.toLowerCase();

    link.assignments.forEach(assignment => {
      if (!adaptedAssignmentsByCourseType[courseTypeLink]) {
        adaptedAssignmentsByCourseType[courseTypeLink] = {};
      }

      /**
       * Important: Assignation type should always be in lowercase
       */
      const assignationType = assignment.assignation_type.toLowerCase();
      if (!adaptedAssignmentsByCourseType[courseTypeLink][assignationType]) {
        adaptedAssignmentsByCourseType[courseTypeLink][assignationType] = {
          assignation_type: assignationType,
          max_assignments_by_link: assignment.max_assignments_by_link,
          course_type_link: courseTypeLink,
        };
      }
    });
  });

  useSettingsStore.setState({
    ruleCourseTypeSetting: setting,
    adaptedAssignmentsByCourseType: adaptedAssignmentsByCourseType,
  });
};

export const setIsProgrammingStructuresEnabled = (setting: RuleCourseTypeSetting) => {
  useSettingsStore.setState({
    isProgrammingStructuresEnabled: setting.enabled,
  });
};

export const setCanPublishPackages = (value: boolean) => {
  useSettingsStore.setState({ canPublishPackages: value });
};
